import { openDB, type IDBPDatabase } from 'idb';
import type {
  EvaluationReport,
  WatchlistItem,
  AppSettings,
  CacheEntry,
  AnnualIncomeStatement,
  AnnualBalanceSheet,
  AnnualCashFlow,
  StockOverview,
  PriceQuote,
} from '@/types/stock';
import { DEFAULT_APP_SETTINGS } from '@/types/stock';

const DB_NAME = 'stock-fundamentals';
const DB_VERSION = 1;

// ============================================================
// Store names
// ============================================================
const STORES = {
  EVALUATIONS: 'evaluations',
  WATCHLIST: 'watchlist',
  SETTINGS: 'settings',
  // API caches
  INCOME_CACHE: 'cache_income',
  BALANCE_CACHE: 'cache_balance',
  CASHFLOW_CACHE: 'cache_cashflow',
  OVERVIEW_CACHE: 'cache_overview',
  PRICE_CACHE: 'cache_price',
} as const;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Evaluations: keyed by id, indexed by ticker
        if (!db.objectStoreNames.contains(STORES.EVALUATIONS)) {
          const evalStore = db.createObjectStore(STORES.EVALUATIONS, { keyPath: 'id' });
          evalStore.createIndex('ticker', 'ticker');
          evalStore.createIndex('evaluationDate', 'evaluationDate');
        }
        // Watchlist: keyed by ticker
        if (!db.objectStoreNames.contains(STORES.WATCHLIST)) {
          db.createObjectStore(STORES.WATCHLIST, { keyPath: 'ticker' });
        }
        // Settings: single record with key 'app'
        if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
          db.createObjectStore(STORES.SETTINGS);
        }
        // Cache stores: keyed by ticker
        for (const storeName of [
          STORES.INCOME_CACHE,
          STORES.BALANCE_CACHE,
          STORES.CASHFLOW_CACHE,
          STORES.OVERVIEW_CACHE,
          STORES.PRICE_CACHE,
        ] as const) {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'ticker' });
          }
        }
      },
    });
  }
  return dbPromise;
}

// ============================================================
// Evaluations
// ============================================================

export async function saveEvaluation(report: EvaluationReport): Promise<void> {
  const db = await getDb();
  await db.put(STORES.EVALUATIONS, report);
}

export async function getEvaluation(id: string): Promise<EvaluationReport | undefined> {
  const db = await getDb();
  return db.get(STORES.EVALUATIONS, id);
}

export async function getEvaluationsByTicker(ticker: string): Promise<EvaluationReport[]> {
  const db = await getDb();
  return db.getAllFromIndex(STORES.EVALUATIONS, 'ticker', ticker);
}

export async function getAllEvaluations(): Promise<EvaluationReport[]> {
  const db = await getDb();
  return db.getAll(STORES.EVALUATIONS);
}

export async function deleteEvaluation(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORES.EVALUATIONS, id);
}

// ============================================================
// Watchlist
// ============================================================

export async function saveWatchlistItem(item: WatchlistItem): Promise<void> {
  const db = await getDb();
  await db.put(STORES.WATCHLIST, item);
}

export async function getWatchlist(): Promise<WatchlistItem[]> {
  const db = await getDb();
  return db.getAll(STORES.WATCHLIST);
}

export async function removeWatchlistItem(ticker: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORES.WATCHLIST, ticker);
}

export async function getWatchlistItem(ticker: string): Promise<WatchlistItem | undefined> {
  const db = await getDb();
  return db.get(STORES.WATCHLIST, ticker);
}

// ============================================================
// Settings
// ============================================================

export async function loadSettings(): Promise<AppSettings> {
  const db = await getDb();
  const settings = await db.get(STORES.SETTINGS, 'app');
  return settings ?? DEFAULT_APP_SETTINGS;
}

export async function saveSettings(settings: AppSettings): Promise<void> {
  const db = await getDb();
  await db.put(STORES.SETTINGS, settings, 'app');
}

// ============================================================
// Cache helpers
// ============================================================

const CACHE_TTL_FINANCIAL = 24 * 60 * 60 * 1000;  // 24h for financial statements
const CACHE_TTL_PRICE = 15 * 60 * 1000;             // 15 min for prices
const CACHE_TTL_OVERVIEW = 6 * 60 * 60 * 1000;      // 6h for overview

interface CacheRow<T> {
  ticker: string;
  data: T;
  fetchedAt: number;
  expiresAt: number;
}

async function getCache<T>(store: string, ticker: string): Promise<T | null> {
  const db = await getDb();
  const row: CacheRow<T> | undefined = await db.get(store, ticker.toUpperCase());
  if (!row) return null;
  if (Date.now() > row.expiresAt) return null;
  return row.data;
}

async function setCache<T>(store: string, ticker: string, data: T, ttl: number): Promise<void> {
  const db = await getDb();
  const now = Date.now();
  const row: CacheRow<T> = {
    ticker: ticker.toUpperCase(),
    data,
    fetchedAt: now,
    expiresAt: now + ttl,
  };
  await db.put(store, row);
}

export async function getCachedIncome(ticker: string): Promise<AnnualIncomeStatement[] | null> {
  return getCache<AnnualIncomeStatement[]>(STORES.INCOME_CACHE, ticker);
}

export async function setCachedIncome(ticker: string, data: AnnualIncomeStatement[]): Promise<void> {
  return setCache(STORES.INCOME_CACHE, ticker, data, CACHE_TTL_FINANCIAL);
}

export async function getCachedBalance(ticker: string): Promise<AnnualBalanceSheet[] | null> {
  return getCache<AnnualBalanceSheet[]>(STORES.BALANCE_CACHE, ticker);
}

export async function setCachedBalance(ticker: string, data: AnnualBalanceSheet[]): Promise<void> {
  return setCache(STORES.BALANCE_CACHE, ticker, data, CACHE_TTL_FINANCIAL);
}

export async function getCachedCashFlow(ticker: string): Promise<AnnualCashFlow[] | null> {
  return getCache<AnnualCashFlow[]>(STORES.CASHFLOW_CACHE, ticker);
}

export async function setCachedCashFlow(ticker: string, data: AnnualCashFlow[]): Promise<void> {
  return setCache(STORES.CASHFLOW_CACHE, ticker, data, CACHE_TTL_FINANCIAL);
}

export async function getCachedOverview(ticker: string): Promise<StockOverview | null> {
  return getCache<StockOverview>(STORES.OVERVIEW_CACHE, ticker);
}

export async function setCachedOverview(ticker: string, data: StockOverview): Promise<void> {
  return setCache(STORES.OVERVIEW_CACHE, ticker, data, CACHE_TTL_OVERVIEW);
}

export async function getCachedPrice(ticker: string): Promise<PriceQuote | null> {
  return getCache<PriceQuote>(STORES.PRICE_CACHE, ticker);
}

export async function setCachedPrice(ticker: string, data: PriceQuote): Promise<void> {
  return setCache(STORES.PRICE_CACHE, ticker, data, CACHE_TTL_PRICE);
}

// ============================================================
// Export cache entry type
// ============================================================
export type { CacheEntry };
